import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import DateRangePicker from '@risk-smart/themed-cloudscape-components/date-range-picker';
import { useAmplitude } from '@risksmart-app/components/src/segment/useAnalytics';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { createDefaultValidator } from '@/components/form/controlled-date-range-picker/dateRangePickerUtils';

import {
  DEFAULT_INDICATOR_RESULTS_RANGE,
  INDICATOR_RESULTS_RELATIVE_OPTIONS,
  isNotFutureDate,
} from './dateRange';

interface Props {
  value: DateRangePickerProps.Value;
  onChange: (value: DateRangePickerProps.Value) => void;
}

/**
 * Date range picker for the indicator results chart (CUS-142). Wraps the themed
 * Cloudscape DateRangePicker, which commits only on Apply (so selecting a range
 * does not move the chart) and reverts pending edits when dismissed without
 * applying.
 *
 * "Reset to default" reuses the picker's built-in clear control, relabelled and
 * coalesced to the Last-6-months default — Cloudscape exposes no pending-state
 * hook, so reset applies immediately rather than staging an un-applied
 * selection.
 */
export const ResultsDateRangePicker: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation('common');
  const translations = t('dateRangeInput', { returnObjects: true });
  const amplitude = useAmplitude();

  const apply = (next: DateRangePickerProps.Value | null) => {
    const isReset = next === null;
    const applied = next ?? DEFAULT_INDICATOR_RESULTS_RANGE;
    onChange(applied);
    void amplitude.track({
      event_type: 'Indicator Results Date Range Applied',
      event_properties: {
        range_type: applied.type,
        is_reset: isReset,
        ...(applied.type === 'relative'
          ? {
              relative_key: applied.key,
              amount: applied.amount,
              unit: applied.unit,
            }
          : { start_date: applied.startDate, end_date: applied.endDate }),
      },
    });
  };

  return (
    <DateRangePicker
      value={value}
      onChange={(e) => apply(e.detail.value)}
      relativeOptions={INDICATOR_RESULTS_RELATIVE_OPTIONS}
      isValidRange={createDefaultValidator(t)}
      isDateEnabled={(date) => isNotFutureDate(date)}
      dateOnly
      absoluteFormat={'long-localized'}
      hideTimeOffset={true}
      i18nStrings={{
        formatRelativeRange: ({ unit, amount }) =>
          t(`relativeTimes.previous.${unit}`, { count: -amount }),
        ...translations,
        clearButtonLabel: t('indicator_results.date_range.reset_to_default'),
      }}
    />
  );
};

export default ResultsDateRangePicker;
